import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "accordion-image-gallery__list" }
const _hoisted_3 = ["aria-expanded", "aria-controls", "onPointerenter", "onClick"]
const _hoisted_4 = { class: "accordion-image-gallery__card-head" }
const _hoisted_5 = { class: "accordion-image-gallery__card-copy" }
const _hoisted_6 = {
  key: 0,
  class: "accordion-image-gallery__initial"
}
const _hoisted_7 = { class: "accordion-image-gallery__heading" }
const _hoisted_8 = { class: "accordion-image-gallery__detail-wrap" }
const _hoisted_9 = ["id"]
const _hoisted_10 = { key: 0 }
const _hoisted_11 = {
  key: 1,
  class: "accordion-image-gallery__body"
}
const _hoisted_12 = { class: "accordion-image-gallery__media" }
const _hoisted_13 = ["src", "alt"]

import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'

export interface AccordionImageGalleryItem {
  key: string
  title: string
  summary?: string
  description?: string
  detail?: string | string[]
  body?: string | string[]
  subtitles?: string[]
  detailHighlights?: string[]
  image: string
  imageAlt?: string
  imagePosition?: string
  initial?: string
}

interface TextSegment {
  text: string
  highlighted: boolean
  index: number
  noWrap?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AccordionImageGallery',
  props: {
    items: {},
    ariaLabel: { default: '' },
    defaultActiveKey: { default: '' },
    showListMarkers: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props

const getFirstKey = () => props.items[0]?.key || ''

const activeKey = ref(props.defaultActiveKey || getFirstKey())
const { locale } = useI18n()
const isEnglish = computed(() => locale.value === 'en')

const activeItem = computed(() => {
  return props.items.find((item) => item.key === activeKey.value) || props.items[0]
})

const currentKey = computed(() => {
  return props.items.some((item) => item.key === activeKey.value) ? activeKey.value : getFirstKey()
})

const galleryStyle = computed(() => ({
  '--accordion-item-count': String(Math.max(props.items.length, 1)),
  '--accordion-row-tracks': props.items.length
    ? props.items
      .map((item) => item.key === currentKey.value ? 'var(--accordion-card-expanded-height)' : 'var(--accordion-card-collapsed-height)')
      .join(' ')
    : '1fr'
}))

const previewFor = (item: AccordionImageGalleryItem) => {
  return item.summary?.trim() || ''
}

const isTitleVisible = (item: AccordionImageGalleryItem) => {
  return currentKey.value === item.key || !previewFor(item)
}

const contentFor = (item: AccordionImageGalleryItem) => {
  if (Array.isArray(item.detail)) return item.detail
  if (typeof item.detail === 'string' && item.detail.trim()) return [item.detail]
  if (Array.isArray(item.body)) return item.body
  if (typeof item.body === 'string' && item.body.trim()) return [item.body]
  if (item.subtitles?.length) return item.subtitles
  if (typeof item.description === 'string' && item.description.trim()) return [item.description]
  if (typeof item.summary === 'string' && item.summary.trim()) return [item.summary]
  return []
}

const isListBody = (item: AccordionImageGalleryItem) => {
  return contentFor(item).length > 1
}

const splitNoWrapSegments = (text: string, highlighted: boolean, startIndex: number): TextSegment[] => {
  const pattern = new RegExp(
    '(\\d+(?:\\.\\d+)?(?:\\s?(?:[\\u4E00-\\u9FFF]+|[A-Za-z%]+|cm|mm|km|kg|Hz|GB|MB|TB|\\u2103|\\u2109|\\u00B0))+)',
    'g'
  )
  const segments: TextSegment[] = []
  let cursor = 0
  let segmentIndex = startIndex
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    const matchText = match[0]
    const matchIndex = match.index

    if (matchIndex > cursor) {
      segments.push({
        text: text.slice(cursor, matchIndex),
        highlighted,
        index: segmentIndex++
      })
    }

    segments.push({
      text: matchText,
      highlighted,
      index: segmentIndex++,
      noWrap: true
    })

    cursor = matchIndex + matchText.length
  }

  if (cursor < text.length) {
    segments.push({
      text: text.slice(cursor),
      highlighted,
      index: segmentIndex++
    })
  }

  return segments.length ? segments : [{ text, highlighted, index: startIndex }]
}

const detailSegmentsFor = (item: AccordionImageGalleryItem): TextSegment[] => {
  const text = contentFor(item)[0] || ''
  const highlights = item.detailHighlights?.map((highlight) => highlight.trim()).filter(Boolean) || []

  if (!text || !highlights.length) {
    return splitNoWrapSegments(text, false, 0)
  }

  const segments: TextSegment[] = []
  let cursor = 0
  let segmentIndex = 0

  while (cursor < text.length) {
    let nextIndex = -1
    let nextHighlight = ''

    highlights.forEach((highlight) => {
      const matchIndex = text.indexOf(highlight, cursor)
      if (matchIndex !== -1 && (nextIndex === -1 || matchIndex < nextIndex)) {
        nextIndex = matchIndex
        nextHighlight = highlight
      }
    })

    if (nextIndex === -1 || !nextHighlight) {
      segments.push(...splitNoWrapSegments(text.slice(cursor), false, segmentIndex))
      break
    }

    if (nextIndex > cursor) {
      const plainSegments = splitNoWrapSegments(text.slice(cursor, nextIndex), false, segmentIndex)
      segments.push(...plainSegments)
      segmentIndex = plainSegments[plainSegments.length - 1].index + 1
    }

    const highlightSegments = splitNoWrapSegments(nextHighlight, true, segmentIndex)
    segments.push(...highlightSegments)
    segmentIndex = highlightSegments[highlightSegments.length - 1].index + 1
    cursor = nextIndex + nextHighlight.length
  }

  return segments
}

let hoverActivationTimer: ReturnType<typeof window.setTimeout> | null = null

const cancelHoverActivation = () => {
  if (hoverActivationTimer !== null) {
    window.clearTimeout(hoverActivationTimer)
    hoverActivationTimer = null
  }
}

const activateKey = (key: string) => {
  cancelHoverActivation()
  if (key !== activeKey.value) {
    activeKey.value = key
  }
}

const scheduleActivate = (key: string) => {
  cancelHoverActivation()
  hoverActivationTimer = window.setTimeout(() => {
    activateKey(key)
  }, 60)
}

onBeforeUnmount(() => {
  cancelHoverActivation()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["accordion-image-gallery", {
      'accordion-image-gallery--with-list-markers': _ctx.showListMarkers,
      'accordion-image-gallery--en': isEnglish.value
    }]),
    "aria-label": _ctx.ariaLabel,
    style: _normalizeStyle(galleryStyle.value)
  }, [
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.items, (item) => {
        return (_openBlock(), _createElementBlock("div", {
          key: item.key,
          class: _normalizeClass(["accordion-image-gallery__card", {
          'accordion-image-gallery__card--active': currentKey.value === item.key
        }])
        }, [
          _createElementVNode("button", {
            class: _normalizeClass(["accordion-image-gallery__trigger", {
            'accordion-image-gallery__trigger--active': currentKey.value === item.key
          }]),
            type: "button",
            "aria-expanded": currentKey.value === item.key,
            "aria-controls": `accordion-image-gallery-panel-${item.key}`,
            onPointerenter: ($event: any) => (scheduleActivate(item.key)),
            onPointerleave: cancelHoverActivation,
            onClick: ($event: any) => (activateKey(item.key))
          }, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                (item.initial)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(item.initial), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("h3", {
                    class: _normalizeClass(["accordion-image-gallery__title", { 'accordion-image-gallery__title--hidden': !isTitleVisible(item) }])
                  }, _toDisplayString(item.title), 3),
                  (previewFor(item))
                    ? (_openBlock(), _createElementBlock("p", {
                        key: 0,
                        class: _normalizeClass(["accordion-image-gallery__summary", { 'accordion-image-gallery__summary--hidden': isTitleVisible(item) }])
                      }, _toDisplayString(previewFor(item)), 3))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_8, [
                  (contentFor(item).length)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        id: `accordion-image-gallery-panel-${item.key}`,
                        class: "accordion-image-gallery__detail"
                      }, [
                        (isListBody(item))
                          ? (_openBlock(), _createElementBlock("ul", _hoisted_10, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(contentFor(item), (subtitle) => {
                                return (_openBlock(), _createElementBlock("li", { key: subtitle }, _toDisplayString(subtitle), 1))
                              }), 128))
                            ]))
                          : (_openBlock(), _createElementBlock("p", _hoisted_11, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(detailSegmentsFor(item), (segment) => {
                                return (_openBlock(), _createElementBlock("span", {
                                  key: `${item.key}-${segment.index}`,
                                  class: _normalizeClass({
                        'accordion-image-gallery__body-highlight': segment.highlighted,
                        'accordion-image-gallery__body-nowrap': segment.noWrap
                      })
                                }, _toDisplayString(segment.text), 3))
                              }), 128))
                            ]))
                      ], 8, _hoisted_9))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createElementVNode("span", {
                class: _normalizeClass(["accordion-image-gallery__chevron", {
                'accordion-image-gallery__chevron--active': currentKey.value === item.key
              }]),
                "aria-hidden": "true"
              }, _cache[0] || (_cache[0] = [
                _createElementVNode("span", { class: "accordion-image-gallery__chevron-icon" }, null, -1)
              ]), 2)
            ])
          ], 42, _hoisted_3)
        ], 2))
      }), 128))
    ]),
    _createElementVNode("figure", _hoisted_12, [
      _createVNode(_Transition, { name: "accordion-image-gallery__image" }, {
        default: _withCtx(() => [
          (_openBlock(), _createElementBlock("img", {
            key: currentKey.value,
            class: "accordion-image-gallery__image",
            src: activeItem.value?.image,
            alt: activeItem.value?.imageAlt || activeItem.value?.title || '',
            style: _normalizeStyle({ objectPosition: activeItem.value?.imagePosition || '' })
          }, null, 12, _hoisted_13))
        ]),
        _: 1
      })
    ])
  ], 14, _hoisted_1))
}
}

})